import React, { useEffect } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import {
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Box,
	Typography,
} from "@mui/material";
import moment from "moment";

import DropdownController from "../../components/inputs/DropdownController";
import DatePickerController from "../../components/inputs/DatePickerController";
import TextFieldController from "../../components/inputs/TextFieldController";
import PATCloseButton from "../../components/Buttons/PATCloseButton";
import PATSaveButton from "../../components/Buttons/PATSaveButton";
import { getMileageWarnType, maxLengthMessage } from "../../utils/functions";

// Validation Schema
const workOrderSchema = yup.object().shape({
	vehicleID: yup.string().nullable().required("Vehicle is required"),
	type: yup.string().nullable().required("Type is required"),
	serviceRequirementID: yup.string().nullable(),
	status: yup
		.string()
		.oneOf(["Open", "Closed"])
		.required("Status is required"),
	assignedToID: yup.string().nullable(),
	vendor: yup.string().trim().max(100, maxLengthMessage(100)).nullable(),
	cost: yup
		.number()
		.nullable()
		.transform((v, o) => (o === "" ? null : v)),
	odometer: yup
		.number()
		.nullable()
		.transform((v, o) => (o === "" ? null : v)),
	openedDate: yup.string().required("Opened date is required"),
	closedDate: yup.string().when("status", {
		is: "Closed",
		then: yup.string().required("Closed date is required"),
	}),
	notes: yup.string().trim().max(500, maxLengthMessage(500)).nullable(),
	fundingSourceID: yup.string().nullable(),
	newVehicleStatus: yup.string().nullable(),
});

const buildDefaults = (initialData, vehicles, codes, fundingSources) => ({
	vehicleID: initialData?.vehicleID ? String(initialData.vehicleID) : "",
	type: initialData?.type || "",
	serviceRequirementID: initialData?.serviceRequirementID
		? String(initialData.serviceRequirementID)
		: "",
	status: initialData?.status || "Open",
	assignedToID: initialData?.assignedToID
		? String(initialData.assignedToID)
		: "",
	vendor: initialData?.vendor || "",
	cost: initialData?.cost || "",
	odometer: initialData?.odometer || "",
	openedDate: initialData?.openedDate || moment().format("YYYY-MM-DD"),
	closedDate: initialData?.closedDate || "",
	notes: initialData?.notes || "",
	fundingSourceID: initialData?.fundingSourceID
		? String(initialData.fundingSourceID)
		: "",
	newVehicleStatus: initialData?.newVehicleStatus || "",
});

const MileageWarn = ({ entered, current }) => {
	const type = getMileageWarnType(entered, current);
	if (!type) return null;
	const isLow = type === "low";
	return (
		<div
			style={{
				marginTop: 6,
				padding: "7px 10px",
				borderRadius: "var(--radius)",
				background: isLow
					? "rgba(239,68,68,0.08)"
					: "rgba(245,166,35,0.10)",
				border: `1px solid ${isLow ? "rgba(239,68,68,0.35)" : "rgba(245,166,35,0.4)"}`,
				fontSize: 11,
				fontWeight: 600,
				color: isLow ? "var(--red)" : "var(--accent)",
				display: "flex",
				alignItems: "center",
				gap: 6,
			}}
		>
			{isLow ? "⚠️" : "⚠️"}
			{isLow
				? `Below current odometer (${(+current).toLocaleString()} mi) — verify reading`
				: `${(parseFloat(entered) - current).toLocaleString()} mi above current odometer — verify reading`}
		</div>
	);
};

const AddEditWorkOrderDialog = ({
	open,
	onClose,
	onSave,
	editWO,
	viewOnly = false,
	vehicles = [],
	workOrderTypes = [],
	employees = [],
	fundingSources = [],
	codes,
}) => {
	const isEdit = !!editWO;
	const isViewOnly = viewOnly || isEdit; // Vehicle cannot be changed after creation

	const {
		control,
		handleSubmit,
		reset,
		watch,
		setValue,
		formState: { errors },
	} = useForm({
		resolver: yupResolver(workOrderSchema),
		defaultValues: buildDefaults(editWO, vehicles, codes, fundingSources),
	});

	// Reset form when dialog opens with new data
	useEffect(() => {
		if (open) {
			reset(buildDefaults(editWO, vehicles, codes, fundingSources));
		}
	}, [open, editWO, reset]);

	// Reset on close
	useEffect(() => {
		if (!open) reset();
	}, [open, reset]);

	// Watched values
	const vehicleID = watch("vehicleID");
	const status = watch("status");
	const odometer = watch("odometer");

	const selectedVehicle = vehicles.find((v) => String(v.id) === vehicleID);
	const vehicleOdometer = selectedVehicle?.odometer;

	const serviceReqOptions = (() => {
		if (!selectedVehicle) return [];
		const allReqs = (codes?.serviceRequirements || []).filter(
			(r) => r.isActive !== false,
		);
		return allReqs
			.filter((req) =>
				(selectedVehicle.serviceRequirements || []).includes(req.id),
			)
			.map((req) => ({
				value: String(req.id),
				label: `${req.label} — every ${req.numericValue.toLocaleString()} ${req.unit}`,
			}));
	})();

	const vehicleStatusOptions = (codes?.vehicleStatuses || [])
		.filter((s) => s.isActive !== false)
		.map((s) => ({ value: s, label: s }));

	const onSubmit = (data) => {
		const payload = {
			...data,
			vehicleID: data.vehicleID ? Number(data.vehicleID) : null,
			assignedToID: data.assignedToID ? Number(data.assignedToID) : null,
			serviceRequirementID: data.serviceRequirementID
				? Number(data.serviceRequirementID)
				: null,
			fundingSourceID: data.fundingSourceID
				? Number(data.fundingSourceID)
				: null,
			cost: data.cost || null,
			odometer: data.odometer || null,
		};
		onSave(payload);
		onClose();
	};

	const handlePrint = () => {
		// Your existing printWO function
	};

	const dialogTitle = isEdit
		? viewOnly
			? `View Work Order — WO-${String(editWO.id).slice(-4).padStart(4, "0")}`
			: `Edit Work Order — WO-${String(editWO.id).slice(-4).padStart(4, "0")}`
		: "New Work Order";

	return (
		<Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
			<DialogTitle>{dialogTitle}</DialogTitle>

			<DialogContent
				sx={{
					pointerEvents: isViewOnly ? "none" : undefined,
					opacity: isViewOnly ? 0.78 : undefined,
					pt: 2,
                    marginTop: "15px",
				}}
			>
				<Box
					component="form"
					onSubmit={handleSubmit(onSubmit)}
					noValidate
					sx={{
						display: "grid",
						gridTemplateColumns: "repeat(2, 1fr)",
						gap: 2,
					}}
				>
					{/* Vehicle */}
					<Box sx={{ gridColumn: "1 / -1" }}>
						<DropdownController
							name="vehicleID"
							label="Vehicle *"
							options={vehicles
								.filter((v) => v.status !== "Retired")
								.map((v) => ({
									value: String(v.id),
									label: `${v.unitNumber} — ${v.year} ${v.make} ${v.model}`,
								}))}
							control={control}
							errors={errors}
							disabled={isEdit} // Vehicle cannot be changed after creation
						/>
						{isEdit && (
							<Typography
								variant="caption"
								color="text.secondary"
								sx={{ mt: 0.5, display: "block" }}
							>
								Vehicle cannot be changed after creation
							</Typography>
						)}
					</Box>

					{/* Type */}
					<DropdownController
						name="type"
						label="Type *"
						options={workOrderTypes.map((t) => ({
							value: t,
							label: t,
						}))}
						control={control}
						errors={errors}
					/>

					{/* Fulfills Service Requirement */}
					<DropdownController
						name="serviceRequirementID"
						label="Fulfills Service Requirement"
						options={serviceReqOptions}
						control={control}
						errors={errors}
						placeholder="None / N/A"
					/>

					{/* Status */}
					<DropdownController
						name="status"
						label="Status *"
						options={[
							{ value: "Open", label: "Open" },
							{ value: "Closed", label: "Closed" },
						]}
						control={control}
						errors={errors}
						onChange={(e) => {
							// Reset closedDate if status changes to Open
							if (e.target.value === "Open") {
								setValue("closedDate", "");
							}
						}}
					/>

					{/* Assigned To */}
					<DropdownController
						name="assignedToID"
						label="Assigned To (optional)"
						options={(employees || [])
							.filter((e) => e.isActive)
							.map((e) => ({
								value: String(e.id),
								label: `${e.badge} — ${e.rank} ${e.firstName} ${e.lastName}`,
							}))}
						control={control}
						errors={errors}
						placeholder="— Unassigned —"
					/>

					{/* Vendor */}
					<TextFieldController
						name="vendor"
						label="Vendor"
						control={control}
						errors={errors}
						placeholder="Repair shop / vendor"
					/>

					{/* Cost */}
					<TextFieldController
						name="cost"
						label="Cost ($)"
						control={control}
						errors={errors}
						type="number"
						placeholder="0.00"
					/>

					{/* Odometer */}
					<TextFieldController
						name="odometer"
						label="Odometer Reading (mi)"
						control={control}
						errors={errors}
						type="number"
						placeholder={selectedVehicle?.odometer || "Optional"}
					/>
					{vehicleOdometer && odometer && (
						<MileageWarn
							entered={odometer}
							current={vehicleOdometer}
						/>
					)}

					{/* Opened Date */}
					<DatePickerController
						name="openedDate"
						label="Opened Date *"
						control={control}
						errors={errors}
					/>

					{/* Closed Date (only when status=Closed) */}
					{status === "Closed" && (
						<DatePickerController
							name="closedDate"
							label="Closed Date *"
							control={control}
							errors={errors}
						/>
					)}

					{/* Notes */}
					<Box sx={{ gridColumn: "1 / -1" }}>
						<TextFieldController
							name="notes"
							label="Notes"
							control={control}
							errors={errors}
							multiline
							rows={3}
							placeholder="Describe the work required..."
						/>
					</Box>

					{/* Funding Source */}
					<DropdownController
						name="fundingSourceID"
						label="Funding Source (optional)"
						options={(fundingSources || []).map((fs) => ({
							value: String(fs.id),
							label: fs.title,
						}))}
						control={control}
						errors={errors}
						placeholder="— Not linked to funding source —"
					/>

					{/* Vehicle Status (only when vehicle selected) */}
					{selectedVehicle && (
						<Box
							sx={{
								gridColumn: "1 / -1",
								pt: 2,
								mt: 1,
								borderTop: "1px solid",
								borderColor: "divider",
							}}
						>
							<Typography
								variant="caption"
								fontWeight={700}
								color="text.secondary"
								sx={{
									textTransform: "uppercase",
									letterSpacing: "0.06em",
									mb: 1,
									display: "block",
								}}
							>
								Vehicle Status
							</Typography>
							<Box
								sx={{
									display: "flex",
									alignItems: "center",
									flexWrap: "wrap",
									gap: 2,
								}}
							>
								<Typography
									variant="body2"
									color="text.secondary"
								>
									Current:
									<strong
										style={{
											color:
												selectedVehicle.status ===
												"Out of Service"
													? "error.main"
													: selectedVehicle.status ===
														  "Maintenance"
														? "warning.main"
														: "success.main",
											marginLeft: "0.5ch",
										}}
									>
										{selectedVehicle.status}
									</strong>
								</Typography>
								<DropdownController
									name="newVehicleStatus"
									label="Set New Status"
									options={vehicleStatusOptions}
									control={control}
									errors={errors}
									size="small"
									sx={{ minWidth: 200 }}
								/>
							</Box>
						</Box>
					)}
				</Box>
			</DialogContent>

			<DialogActions sx={{ px: 3, pb: 2, justifyContent: "flex-end" }}>
				{editWO && (
					<button
						className="btn btn-ghost"
						onClick={handlePrint}
						title="Print work order"
						style={{ marginRight: "auto" }}
					>
						🖨️ Print
					</button>
				)}
				<PATCloseButton onClick={onClose} tooltipText="Cancel" />
				{!isViewOnly && (
					<PATSaveButton
						onClick={handleSubmit(onSubmit)}
						tooltipText={
							isEdit ? "Save Changes" : "Create Work Order"
						}
					/>
				)}
			</DialogActions>
		</Dialog>
	);
};

export default AddEditWorkOrderDialog;
